import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Button — the VELUX block-UI colour logic, same as the flow's `PillButton`:
 * brand Yellow moves you forward (primary, black type), brand Black is
 * structure (secondary strokes, focus ring), red only ever means destructive.
 * Radii come from the theme's 2/4px scale — no capsules here.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-daylight text-primary hover:bg-daylight-pressed active:bg-daylight-pressed",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        /*
         * `outline` and `secondary` are the same role in this system — the
         * black-stroke companion to the yellow CTA. Both names stay so existing
         * call sites keep working; there is deliberately no third, tinted step.
         */
        outline:
          "border-primary text-primary border bg-transparent hover:bg-primary/5 active:bg-primary/5",
        secondary:
          "border-primary text-primary border bg-transparent hover:bg-primary/5 active:bg-primary/5",
        ghost: "text-primary hover:bg-primary/5",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
